import { subMonths, format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { remapParamsToAPI } from '../utils/remapParamsToAPI';
import api from './apiClient';

export const useProjectsQuery = (fullProjectsQuery, action, queryOptions) => {
  const token = useSelector((state) => state.auth.token);
  const locale = useSelector((state) => state.preferences['locale']);

  const fetchProjects = (signal, queryKey) => {
    const [, fullProjectsQuery, action] = queryKey;
    const paramsRemapped = remapParamsToAPI(fullProjectsQuery, backendToQueryConversion);
    // it's needed in order to query by action when the user goes to /explore page
    if (paramsRemapped.action === undefined && action) {
      paramsRemapped.action = action;
    }

    if (paramsRemapped.lastUpdatedTo) {
      paramsRemapped.lastUpdatedTo = format(subMonths(Date.now(), 6), 'yyyy-MM-dd');
    }

    return api(token, locale)
      .get('projects/', {
        signal,
        params: paramsRemapped,
      })
      .then((res) => res.data);
  };

  return useQuery({
    queryKey: ['projects', fullProjectsQuery, action],
    queryFn: ({ signal, queryKey }) => fetchProjects(signal, queryKey),
    keepPreviousData: true,
    ...queryOptions,
  });
};

export const useProjectQuery = (projectId, otherOptions) => {
  const token = useSelector((state) => state.auth.token);
  const locale = useSelector((state) => state.preferences['locale']);
  const fetchProject = ({ signal }) => {
    return api(token, locale).get(`projects/${projectId}/`, {
      signal,
    });
  };

  return useQuery({
    queryKey: ['project', projectId],
    queryFn: fetchProject,
    ...otherOptions,
  });
};
export const useProjectSummaryQuery = (projectId, otherOptions = {}) => {
  const token = useSelector((state) => state.auth.token);
  const locale = useSelector((state) => state.preferences['locale']);

  const fetchProjectSummary = ({ signal }) => {
    return api(token, locale).get(`projects/${projectId}/queries/summary/`, {
      signal,
    });
  };

  return useQuery({
    queryKey: ['project-summary', projectId],
    queryFn: fetchProjectSummary,
    select: (data) => data.data,
    ...otherOptions,
  });
};

export const useProjectContributionsQuery = (projectId, otherOptions = {}) => {
  const token = useSelector((state) => state.auth.token);
  const fetchProjectContributions = ({ signal }) => {
    return api(token).get(`projects/${projectId}/contributions/`, {
      signal,
    });
  };

  return useQuery({
    queryKey: ['project-contributions', projectId],
    queryFn: fetchProjectContributions,
    select: (data) => data.data.userContributions,
    ...otherOptions,
  });
};

export const useProjectContributionsLevelQuery = (otherOptions = {}) => {
  const token = useSelector((state) => state.auth.token);
  const fetchProjectContributionLevels = ({ signal }) => {
    return api(token).get(`levels/`, {
      signal,
    });
  };

  return useQuery({
    queryKey: ['project-contributions-levels'],
    queryFn: fetchProjectContributionLevels,
    select: (data) => data.data.levels,
    ...otherOptions,
  });
};

export const useActivitiesQuery = (projectId) => {
  const token = useSelector((state) => state.auth.token);
  const ACTIVITIES_REFETCH_INTERVAL = 1000 * 60;
  const fetchProjectActivities = ({ signal }) => {
    return api(token).get(`projects/${projectId}/activities/latest/`, {
      signal,
    });
  };

  return useQuery({
    queryKey: ['project-activities', projectId],
    queryFn: fetchProjectActivities,
    select: (data) => data.data,
    refetchIntervalInBackground: false,
    refetchInterval: ACTIVITIES_REFETCH_INTERVAL,
    refetchOnWindowFocus: true,
    useErrorBoundary: true,
  });
};

export const useTasksQuery = (projectId, otherOptions = {}) => {
  const token = useSelector((state) => state.auth.token);
  const fetchProjectTasks = ({ signal }) => {
    return api(token).get(`projects/${projectId}/tasks/`, {
      signal,
    });
  };

  return useQuery({
    queryKey: ['project-tasks', projectId],
    queryFn: fetchProjectTasks,
    select: (data) => data.data,
    ...otherOptions,
  });
};

export const usePriorityAreasQuery = (projectId) => {
  const fetchProjectPriorityArea = (signal) => {
    return api().get(`projects/${projectId}/queries/priority-areas/`, {
      signal,
    });
  };

  return useQuery({
    queryKey: ['project-priority-area', projectId],
    queryFn: ({ signal }) => fetchProjectPriorityArea(signal),
    select: (data) => data.data,
  });
};

export const useProjectTimelineQuery = (projectId) => {
  const fetchTimelineData = (signal) => {
    return api().get(`projects/${projectId}/contributions/queries/day/`, {
      signal,
    });
  };

  return useQuery({
    queryKey: ['project-timeline', projectId],
    queryFn: ({ signal }) => fetchTimelineData(signal),
    select: (data) => data.data.stats,
  });
};

export const useTaskDetail = (projectId, taskId, shouldRefetch) => {
  const token = useSelector((state) => state.auth.token);

  const fetchTaskDetail = ({ signal }) => {
    return api(token).get(`projects/${projectId}/tasks/${taskId}/`, {
      signal,
    });
  };

  return useQuery({
    queryKey: ['task-detail', projectId, taskId],
    queryFn: fetchTaskDetail,
    select: (data) => data.data,
    enabled: !!(projectId && taskId),
    refetchInterval: shouldRefetch ? 1000 * 60 : false,
  });
};

// MAPPING
export const stopMapping = (projectId, taskId, comment, token, locale = 'en') => {
  return api(token, locale).post(`projects/${projectId}/tasks/actions/stop-mapping/${taskId}/`, {
    comment,
  });
};

export const splitTask = (projectId, taskId, token, locale) => {
  return api(token, locale).post(`projects/${projectId}/tasks/actions/split/${taskId}/`);
};

export const submitMappingTask = (url, payload, token, locale) => {
  return api(token, locale).post(url, payload);
};

// VALIDATION
export const stopValidation = (projectId, payload, token, locale = 'en') => {
  return api(token, locale).post(`projects/${projectId}/tasks/actions/stop-validation/`, payload);
};

export const submitValidationTask = (projectId, payload, token, locale) => {
  return api(token, locale).post(
    `projects/${projectId}/tasks/actions/unlock-after-validation/`,
    payload,
  );
};

export const downloadAsCSV = (allQueryParams, action, token) => {
  const paramsRemapped = remapParamsToAPI(allQueryParams, backendToQueryConversion);
  // it's needed in order to query by action
  if (paramsRemapped.action === undefined && action) {
    paramsRemapped.action = action;
  }

  if (paramsRemapped.lastUpdatedTo) {
    paramsRemapped.lastUpdatedTo = format(subMonths(Date.now(), 6), 'yyyy-MM-dd');
  }
  return api(token).get('projects/', {
    params: paramsRemapped,
  });
};

export const useAllPartnersQuery = (token, userId) => {
  const fetchAllPartners = () => {
    return api(token).get('partners/');
  };

  return useQuery({
    queryKey: ['all-partners', userId],
    queryFn: fetchAllPartners,
    select: (response) => response.data,
  });
};

const backendToQueryConversion = {
  difficulty: 'difficulty',
  campaign: 'campaign',
  team: 'teamId',
  organisation: 'organisationName',
  location: 'country',
  types: 'mappingTypes',
  exactTypes: 'mappingTypesExact',
  interests: 'interests',
  text: 'textSearch',
  page: 'page',
  orderBy: 'orderBy',
  orderByType: 'orderByType',
  createdByMe: 'createdByMe',
  managedByMe: 'managedByMe',
  favoritedByMe: 'favoritedByMe',
  mappedByMe: 'mappedByMe',
  status: 'projectStatuses',
  action: 'action',
  stale: 'lastUpdatedTo',
  createdFrom: 'createdFrom',
  basedOnMyInterests: 'basedOnMyInterests',
  partnerId: 'partnerId',
  partnershipFrom: 'partnershipFrom',
  partnershipTo: 'partnershipTo',
};
